import { FC, JSX, useMemo } from 'react';

import classNames from 'classnames';

import { BUTTON_TYPES, EvRadioGroup, EvStep, EvTitle } from '@evinced-private/ui-common';

import { ContactUsButton } from 'src/components/contact-us-button/ContactUsButton';

import { useUserTenant } from '../../../providers/userTenantProvider/UserTenantProvider';

import './Steps.scss';

interface ISetPermissions {
	handleChange: (res: string) => void;
	userPermission: string;
	onContinue?: () => void;
	isStepper?: boolean;
	userEmail?: string;
	className?: string;
}

const selectionTitle = 'Select a user type:';

export const SetPermissions: FC<ISetPermissions> = ({
	handleChange,
	onContinue,
	userPermission,
	isStepper = true,
	userEmail = '',
	className
}) => {
	const { usersRolesListDictionary, getFullRoleItem } = useUserTenant();

	const roleType = usersRolesListDictionary.TENANT_USER;
	const additionalInfo = {
		isRequired: true,
		label: `Please help update the type of user associated with this email address ${userEmail}`
	};

	const renderPermissionsSelection = useMemo((): JSX.Element => {
		const radioGroupOptions = [
			{
				label: getFullRoleItem(roleType).name,
				description: 'Can crawl, scan and use all Site Scanner features.',
				isChecked: userPermission === roleType,
				onClick: () => handleChange(roleType)
			},
			{
				label: getFullRoleItem(usersRolesListDictionary.READ_ONLY).name,
				description: "Can view issues, can't take actions like crawl or scan.",
				isChecked: userPermission === usersRolesListDictionary.READ_ONLY,
				onClick: () => handleChange(usersRolesListDictionary.READ_ONLY)
			}
		];
		return (
			<div className="permissions-options-container">
				<EvRadioGroup
					options={radioGroupOptions}
					name="user-type-radio"
					ariaLabel="User type selection"
				/>
			</div>
		);
	}, [usersRolesListDictionary, roleType, userPermission, handleChange, getFullRoleItem]);

	const renderAdminMessage = (): JSX.Element => {
		return (
			<div className="admin-message">
				<p>
					This user is set as Admin. In order to change the user type, please contact our customer
					support team.
				</p>
				<ContactUsButton
					type={BUTTON_TYPES.SECONDARY}
					title="How can we help?"
					product="Account settings / User management"
					additionalInfo={additionalInfo}
				/>
			</div>
		);
	};

	const setPermissionContent = (): JSX.Element => {
		return (
			<div className="permission-selection-wrapper">
				{userPermission === usersRolesListDictionary.TENANT_ADMIN ? (
					<div>{renderAdminMessage()}</div>
				) : (
					<div className="permission-selection-not-admin">
						{!isStepper && <div className="selection-instructions">{selectionTitle}</div>}
						{renderPermissionsSelection}
					</div>
				)}
			</div>
		);
	};

	if (isStepper) {
		return (
			<EvStep actions={{ continueLabel: 'Send Invite(s)', onNextClicked: onContinue }}>
				<div className="selection-instructions">{selectionTitle}</div>
				{setPermissionContent()}
			</EvStep>
		);
	}

	return (
		<div className={classNames('set-permissions', className)}>
			<EvTitle titleText="Site Scanner permissions" />
			{setPermissionContent()}
		</div>
	);
};
